'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useDeleteUpsells } from '@/lib/api/upsells';
import { useAuth } from '@clerk/clerk-react';

interface DeleteUpsellModalProps {
    upsellId?: string;
    storeId: string;
    onClose: () => void;
}

/**
 * DeleteUpsellModal component for confirming deletion of a single upsell
 */
export const DeleteUpsellModal = ({ upsellId, storeId, onClose }: DeleteUpsellModalProps) => {
    const { t } = useTranslation('upsells');
    const { orgId } = useAuth();
    const deleteUpsellsMutation = useDeleteUpsells();

    const handleDelete = async () => {
        if (!upsellId || !orgId) return;
        await deleteUpsellsMutation.mutateAsync({ ids: [upsellId] });
        onClose();
    };

    return (
        <AlertDialog open={!!upsellId} onOpenChange={onClose}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>{t('modals.delete.title')}</AlertDialogTitle>
                    <AlertDialogDescription>
                        {t('modals.delete.description')}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel onClick={onClose}>{t('actions.cancel')}</AlertDialogCancel>
                    <AlertDialogAction
                        onClick={handleDelete}
                        disabled={deleteUpsellsMutation.isPending}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                        {deleteUpsellsMutation.isPending ? t('modals.delete.deleting') : t('modals.delete.delete')}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};
